/**
 * 房间状态仓库：把服务端推来的事件收敛成一份界面可直接渲染的状态。
 */

import { create } from 'zustand'
import { getPreferred } from '../lib/devices'
import { isDesktop } from '../lib/desktop'
import { autoSelect } from '../lib/lineSelect'
import { currentServer } from '../lib/servers'

import { Client, ClientError, type ConnState } from '../lib/client'
import {
  applyRemoteKey,
  applyRemoteMouse,
  grantRemoteControl,
  revokeRemoteControl,
} from '../lib/desktop'
import { MediaEngine, type RemoteTrack } from '../lib/media'
import { NetMonitor, type NetQuality } from '../lib/netquality'
import { submitReport, type ReportInput } from '../lib/reportApi'
import { useFileStore } from './fileStore'
import { useWhiteboardStore } from './whiteboardStore'
import {
  explainError,
  MsgType,
  type ICEServerConf,
  type MediaKind,
  type MicAssigned,
  type MicLocked,
  type MicNumChanged,
  type MicQueued,
  type MicReleased,
  type RecordStarted,
  type RecordStopped,
  type RecordTick,
  type RoomInfo,
  type RoomJoined,
  type PMInvite,
  type PMInviteReply,
  type PMPeer,
  type RoomPropChanged,
  type ShareCtrlKey,
  type ShareCtrlMouse,
  type ShareStarted,
  type UserRelationChanged,
  type ReactRecv,
  type TextRecv,
  type UserInfo,
  type UserInfoChanged,
  type UserLeft,
  type CaptionState,
  type CaptionText,
  type GroupClosed,
  type GroupNotified,
  type GroupOpened,
  type GroupSpec,
  type PollEnded,
  type AttendEnded,
  type AttendStarted,
  type AttendStat,
  type PollStarted,
  type PollStat,
  type WaitEntry,
  type WaitJoined,
  type WaitList,
  type WaitRejected,
  type UserPropChanged,
  type UserStateChanged,
} from '../lib/messages'
import {
  hasAnyPerm,
  LEAVE_REASON_TEXT,
  LeaveReason,
  MIC_NONE,
  Perm,
  USER_NONE,
  UState,
} from '../lib/protocol'

/** 界面用的用户模型：权限与状态已解析成数值位。 */
export interface Member {
  id: number
  name: string
  info: string
  prop: number
  state: number
  micId: number
  enterTime: number
}

/** 界面用的房间模型。 */
export interface Room {
  id: number
  name: string
  flags: number
  micNum: number
  videoNum: number
  maxUsers: number
  notice: string
  ownerId: number
}

/** 一条聊天消息。 */
/** 表情回应在界面上的一条飘屏。 */
export interface FloatingReaction extends ReactRecv {
  /** React 列表键。同一个人同一秒连点两次也不能撞。 */
  key: string
}

/** 表情在屏幕上停留的时长，与 CSS 动画时长一致。 */
export const REACTION_TTL_MS = 3000

/** 网络质量采样间隔。 */
const NET_SAMPLE_MS = 3000

/** 表情键的自增序号，仅用于去重。 */
let nextReactSeq = 0

export interface ChatMessage extends TextRecv {
  /** 本地生成的唯一键，供列表渲染使用 */
  key: string
}

/** 一条字幕。 */
export interface CaptionLine extends CaptionText {
  /** React 列表键 */
  key: string
}

/** 屏幕上最多同时留几条字幕。 */
export const CAPTION_KEEP = 3

/** 录制状态。 */
export interface RecordingState {
  active: boolean
  recordId?: string
  elapsed: number
  remaining: number
  lastUrl?: string
}

interface RoomState {
  client: Client | null
  media: MediaEngine | null
  /** 随连接绑定的各路监听（白板、文件传输）的退订函数，换连接/离开时逐个调用 */
  unbinds: Array<() => void>
  /** 网络质量采样定时器。离开教室时必须清掉，否则会一直空转。 */
  netTimer: number | null
  conn: ConnState

  selfId: number
  /** 自己的登录账号。分组讨论按账号匹配成员（席位号换房间就变了） */
  selfAccount: string
  setSelfAccount: (account: string) => void
  /** 当前连接令牌，举报等 HTTP 调用要带它做鉴权。 */
  authToken: string
  /** 举报不良内容。App Store 指南 1.2 要求每个用户都能举报。roomId/token 由 store 内部补。 */
  reportContent: (input: Omit<ReportInput, 'roomId' | 'token'>) => Promise<void>
  room: Room | null
  members: Map<number, Member>
  mics: number[]
  micLocks: boolean[]
  micQueue: number[]

  messages: ChatMessage[]
  remoteTracks: Map<string, RemoteTrack>
  speaking: Map<number, number>

  sharerId: number
  /** 当前获准远程控制本机的席位。仅共享方有意义。 */
  controllerId: number
  /** 本机是否正在远程控制别人（值为被控方席位） */
  controllingId: number
  /** 收到的远程控制申请，待本机确认 */
  ctrlRequestFrom: number

  /** 当前私聊对象，USER_NONE 表示不在私聊中 */
  pmPeer: number
  /** 已发出、还没被应答的私聊邀请对象。用来决定要不要显示「撤回」。 */
  pmPending: number
  /** 收到的私聊邀请：席位 → 附言 */
  pmInvites: Map<number, string>
  /** 本机对他人的关系位：席位 → relation */
  relations: Map<number, number>

  recording: RecordingState

  /** 本机是否暂离 */
  away: boolean
  /** 屏幕共享是否已暂停（仅共享方有意义） */
  sharePaused: boolean

  /** 最近一次错误提示，供界面展示 */
  toast: string | null

  // ---- 动作 ----
  /** url 传函数则每次重连都重新解析 —— 桌面端/App 的线路切换靠它 */
  connect: (url: string | (() => string), token: string, ice: ICEServerConf[]) => Promise<void>
  join: (roomId: number, nick?: string, password?: string) => Promise<void>
  leave: () => void
  sendText: (text: string, destId: number, secret?: boolean, broadcast?: boolean) => void
  /** 正在飘的表情，几秒后自动消失。 */
  reactions: FloatingReaction[]
  /** 本机的网络质量，每几秒刷新一次。 */
  net: NetQuality
  /** 当前选中的扬声器 id（'' 为默认）；改它会让所有音频元素重新绑定输出 */
  speakerId: string
  setSpeakerId: (id: string) => void
  /** 被动离开的原因（被踢/结束课程/顶号），0 表示没有；教室页据此退回登录 */
  leftReason: number
  /** 我正在等候室里排队（服务端说的），null 表示不在；admitted 为真表示已放行、该重发 join 了 */
  waiting: (WaitJoined & { admitted?: boolean }) | null
  /** 候场名单，只有主持人收得到 */
  waitList: WaitEntry[]
  /** 被拒绝进入的说明；非空时登录页显示 */
  waitRejected: string | null
  /** 进行中的投票，null 表示没有 */
  poll: PollStarted | null
  /** 进行中投票的实时统计；未开放实时统计时学员收不到 */
  pollStat: PollStat | null
  /** 我已提交的选择（选项下标），空表示还没投 */
  myVote: number[]
  /** 刚结束的投票结果，供弹层展示；关掉后清空 */
  pollResult: PollEnded | null
  startPoll: (title: string, options: string[], opt?: { multi?: boolean; anonymous?: boolean; liveStat?: boolean }) => void
  submitVote: (choices: number[]) => void
  endPoll: () => void
  clearPollResult: () => void

  /** 进行中的签到，null 表示没有 */
  attend: AttendStarted | null
  /** 签到进度。只有主持人收得到 —— 学员不该看到谁还没签 */
  attendStat: AttendStat | null
  /** 我已经签过了 */
  attendMarked: boolean
  /** 刚结束的签到结果，供弹层展示 */
  attendResult: AttendEnded | null
  startAttend: (limitS?: number) => void
  markAttend: () => void
  endAttend: () => void
  clearAttendResult: () => void
  /** 进行中的分组方案，空表示没有分组 */
  groups: GroupSpec[]
  /** 我该去的小组（按账号匹配），null 表示没被分组或我是主持人 */
  myGroup: GroupSpec | null
  /** 分组已结束、该回主房间；带主房间号 */
  groupClosed: number | null
  /** 主持人给各组的最新通知 */
  groupNotice: string | null
  /** 房间是否开着字幕（服务端状态） */
  captionOn: boolean
  /** 最近几条字幕，旧的自动淘汰 */
  captions: CaptionLine[]
  toggleCaption: (on: boolean) => void
  /** 举手 / 放下手。与麦位解耦，自由模式下只是示意 */
  raiseHand: (up: boolean) => void
  /** 会中管控：批量设置全体成员权限（主持人与联席不受影响，服务端保证） */
  setAllUsersProp: (prop: number, opcode: 'add' | 'remove') => void
  /** 解除全体静音，并清掉所有人的举手 */
  unmuteAll: () => void
  openGroups: (count: number) => void
  closeGroups: () => void
  notifyGroups: (text: string) => void
  clearGroupState: () => void
  admitWaiter: (waitId: number, all?: boolean) => void
  rejectWaiter: (waitId: number, reason?: string) => void
  /** 把已入场的成员移回等候室。比踢人温和，可随后再放进来 */
  moveToWaiting: (destId: number) => void
  clearWaiting: () => void
  /** 主持人结束课程：全员退出 */
  endRoom: () => void
  sendReaction: (emoji: string) => void
  requestMic: () => void
  assignMic: (destId: number, micId?: number, force?: boolean) => void
  releaseMic: (destId: number) => void
  lockMic: (micId: number, lock: boolean) => void
  kick: (id: number) => void
  setRoomFlags: (flags: number, opcode: 'add' | 'remove') => void
  setUserProp: (destId: number, prop: number, opcode: 'add' | 'remove') => void
  startRecord: (durationSec: number, name?: string) => void
  stopRecord: () => void
  startShare: () => Promise<void>
  stopShare: () => void
  pauseShare: (paused: boolean) => void

  /** 设置自己的状态位，如暂离、关闭白板接收。 */
  setSelfState: (state: number, opcode: 'add' | 'remove') => void
  /** 切换暂离。 */
  toggleAway: () => void
  /** 上报自己的说话音量，服务端按麦序与阈值决定是否转发。 */
  reportSpeakLevel: (level: number) => void
  /** 设置房间同时可发言的麦位数。 */
  setMicNum: (num: number) => void

  // 私聊
  invitePM: (destId: number, text?: string) => void
  /** 撤回还没被应答的私聊邀请。 */
  cancelPM: (destId: number) => void
  replyPM: (fromId: number, agree: boolean) => void
  stopPM: () => void
  /** 改自己的附加信息（名字后面那行小字）。 */
  setSelfInfo: (info: string) => void
  /** 改名：destId 为自己或（有权限时）别人 */
  renameUser: (destId: number, name: string) => void

  // 屏蔽关系
  setRelation: (destId: number, bits: number, on: boolean) => void

  // 远程控制
  requestControl: () => void
  replyControl: (destId: number, agree: boolean) => void
  sendControlMouse: (ev: ShareCtrlMouse) => void
  sendControlKey: (ev: ShareCtrlKey) => void

  setToast: (msg: string | null) => void
}

/** 把线上用户结构转成界面模型。 */
function toMember(u: UserInfo): Member {
  return {
    id: u.id,
    name: u.name,
    info: u.info ?? '',
    prop: u.prop,
    state: u.state,
    micId: u.micId,
    enterTime: u.enterTime,
  }
}

/** 把线上房间结构转成界面模型。 */
function toRoom(r: RoomInfo): Room {
  return {
    id: r.id,
    name: r.name,
    flags: r.flags,
    micNum: r.micNum,
    videoNum: r.videoNum,
    maxUsers: r.maxUsers,
    notice: r.notice ?? '',
    ownerId: r.ownerId,
  }
}

/** 网络质量的初始值：还没采过样。 */
const NET_UNKNOWN: NetQuality = { level: 'unknown', loss: 0, rtt: -1, text: '正在评估网络…' }

/**
 * 一次会话（一条连接、一个房间）范围内的全部状态及其初始值。
 *
 * leave 与 connect 都以它为准整体复位。以前 leave 手写了一份要清的字段，
 * 漏了投票、录制、暂离、字幕、分组、私聊邀请、屏蔽关系、共享暂停……
 * 于是进了小组还挂着主房间的投票（带着错的 pollId）、显示「录制中」。
 * 以后加会话字段只要加在这里，就不会再漏。
 *
 * 不在这里的是跨会话的偏好：自己的账号（selfAccount）、扬声器选择（speakerId）。
 * 每次调用都返回新的 Map/数组，免得几次会话共用同一个可变对象。
 */
function initialSession() {
  return {
    client: null as Client | null,
    media: null as MediaEngine | null,
    unbinds: [] as Array<() => void>,
    netTimer: null as number | null,
    conn: 'idle' as ConnState,
    selfId: USER_NONE,
    authToken: '',
    room: null as Room | null,
    members: new Map<number, Member>(),
    mics: [] as number[],
    micLocks: [] as boolean[],
    micQueue: [] as number[],
    messages: [] as ChatMessage[],
    remoteTracks: new Map<string, RemoteTrack>(),
    speaking: new Map<number, number>(),
    sharerId: USER_NONE,
    controllerId: USER_NONE,
    controllingId: USER_NONE,
    ctrlRequestFrom: USER_NONE,
    pmPeer: USER_NONE,
    pmPending: USER_NONE,
    pmInvites: new Map<number, string>(),
    relations: new Map<number, number>(),
    recording: { active: false, elapsed: 0, remaining: 0 } as RecordingState,
    away: false,
    sharePaused: false,
    toast: null as string | null,
    reactions: [] as FloatingReaction[],
    net: NET_UNKNOWN,
    leftReason: 0,
    waiting: null as RoomState['waiting'],
    waitList: [] as WaitEntry[],
    waitRejected: null as string | null,
    poll: null as PollStarted | null,
    pollStat: null as PollStat | null,
    myVote: [] as number[],
    pollResult: null as PollEnded | null,
    attend: null as AttendStarted | null,
    attendStat: null as AttendStat | null,
    attendMarked: false,
    attendResult: null as AttendEnded | null,
    groups: [] as GroupSpec[],
    myGroup: null as GroupSpec | null,
    groupClosed: null as number | null,
    groupNotice: null as string | null,
    captionOn: false,
    captions: [] as CaptionLine[],
  } satisfies Partial<RoomState>
}

/**
 * 拆掉当前连接挂着的一切：监听、定时器、媒体、连接本身、远程控制授权。
 * 只拆不改状态，状态由调用方整体复位。
 */
function teardown(s: RoomState): void {
  s.unbinds.forEach((f) => f())
  if (s.netTimer !== null) window.clearInterval(s.netTimer)
  s.media?.dispose()
  s.client?.close()
  // 还授权着别人控制本机的话一并收回：连接都没了，授权不能留在 Rust 那边。
  if (s.controllerId !== USER_NONE) void revokeRemoteControl()
}

let msgSeq = 0
let nextCaptionSeq = 0
/**
 * 判断要不要订阅某人的摄像头。
 *
 * 名额之外的人不订，但主持人与持麦者永远占得到名额 ——
 * 正在讲话的人看不到，是最不能接受的一种「省带宽」。
 */
function shouldSubscribeVideo(s: RoomState, userId: number): boolean {
  const m = s.members.get(userId)
  if (!m) return false
  if (hasAnyPerm(m.prop, Perm.Op) || m.micId >= 0) return true

  // videoNum 是房间允许的同时视频路数；没配置时给一个保守的默认值。
  const quota = s.room?.videoNum && s.room.videoNum > 0 ? s.room.videoNum : 6
  let used = 0
  for (const t of s.remoteTracks.values()) {
    if (t.kind === 'video' && t.userId !== userId) used++
  }
  return used < quota
}

function toMessage(m: TextRecv): ChatMessage {
  return { ...m, key: `${m.time}-${m.fromId}-${msgSeq++}` }
}

export const useRoomStore = create<RoomState>((set, get) => ({
  ...initialSession(),
  selfAccount: '',
  setSelfAccount: (selfAccount) => set({ selfAccount }),
  speakerId: getPreferred().speaker,
  startPoll: (title, options, opt) =>
    get().client?.send(MsgType.PollStart, { title, options, ...opt }),
  submitVote: (choices) => {
    const p = get().poll
    if (!p) return
    get().client?.send(MsgType.PollVote, { pollId: p.pollId, choices })
    // 乐观记下自己的选择：服务端不回单独的应答，等统计回来才知道有没有生效，
    // 但界面要立刻从「选择」切到「已投」，否则会以为没点上而重复提交。
    set({ myVote: choices })
  },
  endPoll: () => {
    const p = get().poll
    if (p) get().client?.send(MsgType.PollEnd, { pollId: p.pollId })
  },
  clearPollResult: () => set({ pollResult: null }),

  startAttend: (limitS) => {
    get().client?.send(MsgType.AttendStart, { limitS: limitS ?? 0 })
  },
  markAttend: () => {
    const a = get().attend
    if (!a || get().attendMarked) return
    // 本地先置上。等服务端回来再改的话，网络慢的时候按钮会像没反应，
    // 学员会反复点 —— 而这一下在课堂上是有心理压力的。
    set({ attendMarked: true })
    get().client?.send(MsgType.AttendMark, { roundId: a.roundId })
  },
  endAttend: () => {
    const a = get().attend
    if (a) get().client?.send(MsgType.AttendEnd, { roundId: a.roundId })
  },
  clearAttendResult: () => set({ attendResult: null }),
  toggleCaption: (on) => get().client?.send(MsgType.CaptionToggle, { on }),
  raiseHand: (up) => get().client?.send(MsgType.HandRaise, { up }),
  setAllUsersProp: (prop, opcode) =>
    get().client?.send(MsgType.MeetingControl, { prop: prop >>> 0, opcode }),
  unmuteAll: () => {
    // 「解除全体静音」= 还回发言权。服务端会顺带清掉举手。
    get().client?.send(MsgType.MeetingControl, { prop: Perm.Speak >>> 0, opcode: 'add' })
  },
  openGroups: (count) => get().client?.send(MsgType.GroupOpen, { count }),
  closeGroups: () => get().client?.send(MsgType.GroupClose),
  notifyGroups: (text) => get().client?.send(MsgType.GroupNotify, { text }),
  clearGroupState: () => set({ groups: [], myGroup: null, groupClosed: null, groupNotice: null }),
  admitWaiter: (waitId, all = false) => get().client?.send(MsgType.WaitAdmit, { waitId, all }),
  rejectWaiter: (waitId, reason) => get().client?.send(MsgType.WaitReject, { waitId, reason }),
  moveToWaiting: (destId) => get().client?.send(MsgType.WaitMoveTo, { destId }),
  clearWaiting: () => set({ waiting: null, waitRejected: null, waitList: [] }),
  endRoom: () => get().client?.send(MsgType.RoomEnd),
  setSpeakerId: (speakerId) => set({ speakerId }),

  async connect(url, token, ice) {
    // 重入时先把上一次的连接整个拆干净（密码输错后再点「进入教室」就是这条路）。
    // 以前只 close 了旧 client：网络采样定时器、白板监听、媒体引擎都留着，
    // 每重试一次就多一份在后台空转。
    teardown(get())
    set(initialSession())

    const client = new Client({
      url,
      token,
      // 同一条线路连续连不上时换备用线路（网页版同源，没得换）。
      // 令牌两台服务器都认（密钥一致），换线后自动 hello + 回房，用户只看到一句提示。
      onFailover: async () => {
        if (!isDesktop()) return false
        const from = currentServer()
        const r = await autoSelect(from.id)
        if (!r.server) return false
        get().setToast(`${from.name}连不上，已切换到${r.server.name}`)
        return true
      },
    })
    const media = new MediaEngine({ client, iceServers: ice })

    media.setHandlers(
      (t) =>
        set((s) => {
          const next = new Map(s.remoteTracks)
          next.set(t.trackId, t)
          return { remoteTracks: next }
        }),
      (trackId) =>
        set((s) => {
          const next = new Map(s.remoteTracks)
          next.delete(trackId)
          return { remoteTracks: next }
        }),
    )

    // 本机音量上报。只在持麦时才真正发出去 —— 服务端也会再挡一道，
    // 但客户端先挡掉能省下大量无用信令：一个 50 人的教室里，
    // 每人每 150ms 报一次就是每秒 300 多条。
    media.setSpeakHandler((level) => {
      const st = get()
      if (st.selfId === USER_NONE) return
      if ((st.members.get(st.selfId)?.micId ?? MIC_NONE) < 0) return
      st.reportSpeakLevel(level)
    })

    // 用户点了浏览器自带的「停止共享」：和点工具栏按钮走同一条路，服务端才知道共享结束了。
    media.setScreenEndedHandler(() => get().stopShare())

    bindEvents(client, set, get)
    // 白板事件必须在进房之前绑好：room.joined 里带着白板快照，
    // 绑晚了就会错过它，中途进来的人看到的是一块空白板。
    const unbindWB = useWhiteboardStore.getState().bind(client)
    // 文件传输的监听同样跟着连接走，而不是跟着文件面板走：面板没打开时也得收得到邀约。
    const unbindFiles = useFileStore.getState().bind(client, {
      selfId: () => get().selfId,
      notify: (text) => set({ toast: text }),
      nameOf: (id) => get().members.get(id)?.name ?? `席位 ${id}`,
    })

    // 网络质量每 3 秒采一次。间隔太短会把瞬时抖动放大成「网络很差」，
    // 太长又赶不上真的卡顿。
    const monitor = new NetMonitor()
    const netTimer = window.setInterval(() => {
      const m = get().media
      if (!m) return
      void monitor.sample(m.peerConnections()).then((q) => set({ net: q }))
    }, NET_SAMPLE_MS)

    set({ client, media, unbinds: [unbindWB, unbindFiles], netTimer, authToken: token })
    await client.connect()
  },

  async join(roomId, nick, password) {
    const { client } = get()
    if (!client) throw new Error('尚未连接')
    client.rememberRoom({ roomId, nick, password })
    try {
      await client.request(MsgType.RoomJoin, { roomId, nick, password }, MsgType.RoomJoined)
    } catch (e) {
      if (e instanceof ClientError) {
        set({ toast: e.message })
      }
      throw e
    }
  },

  leave() {
    const st = get()
    st.client?.send(MsgType.RoomLeave)
    teardown(st)
    // 整体复位到初始状态，而不是挑几个字段清 —— 见 initialSession 的说明。
    set({ ...initialSession(), conn: 'closed' })
  },

  sendText(text, destId, secret = false, broadcast = false) {
    get().client?.send(MsgType.TextSend, { destId, text, secret, broadcast })
  },

  sendReaction(emoji) {
    get().client?.send(MsgType.ReactSend, { emoji })
  },

  requestMic() {
    get().client?.send(MsgType.MicRequest)
  },

  assignMic(destId, micId = MIC_NONE, force = false) {
    get().client?.send(MsgType.MicAssign, { destId, micId, force })
  },

  releaseMic(destId) {
    get().client?.send(MsgType.MicRelease, { destId })
  },

  lockMic(micId, lock) {
    get().client?.send(MsgType.MicLock, { micId, lock })
  },

  kick(id) {
    get().client?.send(MsgType.RoomKick, { id })
  },

  setRoomFlags(flags, opcode) {
    // 转成无符号：等候室位是 1<<31，任何按位运算都会把它变成负数，
    // 而服务端字段是 uint32 —— 负数会 unmarshal 失败，开关静默不生效。
    get().client?.send(MsgType.RoomPropSet, { flags: flags >>> 0, opcode })
  },

  setUserProp(destId, prop, opcode) {
    get().client?.send(MsgType.UserPropSet, { destId, prop, opcode })
  },

  startRecord(durationSec, name) {
    get().client?.send(MsgType.RecordStart, { duration: durationSec, name })
  },

  stopRecord() {
    get().client?.send(MsgType.RecordStop)
  },

  async startShare() {
    const { client, media } = get()
    if (!client || !media) return
    try {
      await media.publishScreen()
      client.send(MsgType.ShareStart, { mode: 'screen' })
    } catch (e) {
      // 用户在系统的共享选择框里点了取消：浏览器报 NotAllowedError（个别是 AbortError），
      // 这不算错误，什么都不用说。其余的（浏览器不支持、没走 HTTPS、系统没给录屏权限）
      // 必须说出来 —— 以前一律吞掉，表现就是「点了共享屏幕没反应」。
      const name = e instanceof DOMException ? e.name : ''
      if (name === 'NotAllowedError' || name === 'AbortError') return
      set({ toast: `无法共享屏幕：${e instanceof Error ? e.message : String(e)}` })
    }
  },

  stopShare() {
    const { client, media } = get()
    client?.send(MsgType.ShareStop)
    void media?.unpublish(['screen'])
    void revokeRemoteControl()
    set({ controllerId: USER_NONE, sharePaused: false })
  },

  pauseShare(paused) {
    const { client, media } = get()
    // 字段名以服务端为准是 pause（protocol.SharePause）。以前这里发的是 paused，
    // 服务端解出来恒为 false，广播给观众的永远是「没暂停」。
    client?.send(MsgType.SharePause, { pause: paused })
    // 光通知别人不够，本地那一路也得真停掉 —— 否则「暂停」只是把
    // 观众的界面糊上一层，画面照旧在往外推，切到私密内容一样会被看见。
    media?.setSharePaused(paused)
    set({ sharePaused: paused })
  },

  setSelfState(state, opcode) {
    get().client?.send(MsgType.UserStateSet, {
      destId: get().selfId,
      state,
      opcode,
    })
  },

  toggleAway() {
    const next = !get().away
    get().setSelfState(UState.Leaving, next ? 'add' : 'remove')
    set({ away: next })
  },

  reportSpeakLevel(level) {
    get().client?.send(MsgType.RTCSpeaking, { userId: get().selfId, level })
  },

  async reportContent(input) {
    const st = get()
    const roomId = st.room?.id
    if (!roomId || !st.authToken) {
      set({ toast: '尚未进入课堂，无法举报' })
      return
    }
    try {
      await submitReport({ ...input, roomId, token: st.authToken })
      set({ toast: '举报已提交，我们会尽快处理' })
    } catch {
      set({ toast: '举报提交失败，请稍后再试' })
    }
  },

  setMicNum(num) {
    get().client?.send(MsgType.MicNumSet, { num })
  },

  invitePM(destId, text) {
    get().client?.send(MsgType.PMInvite, { fromId: get().selfId, toId: destId, text: text ?? '' })
    set({ toast: '已发出私聊邀请，等待对方同意', pmPending: destId })
  },

  cancelPM(destId) {
    get().client?.send(MsgType.PMCancel, { fromId: get().selfId, toId: destId })
    set({ pmPending: USER_NONE, toast: '已撤回私聊邀请' })
  },

  replyPM(fromId, agree) {
    get().client?.send(MsgType.PMInviteReply, { fromId, toId: get().selfId, agree })
    set((s) => {
      const next = new Map(s.pmInvites)
      next.delete(fromId)
      return { pmInvites: next, pmPeer: agree ? fromId : s.pmPeer }
    })
  },

  stopPM() {
    get().client?.send(MsgType.PMStop)
    set({ pmPeer: USER_NONE })
  },

  setSelfInfo(info) {
    get().client?.send(MsgType.UserInfoSet, {
      destId: get().selfId,
      info,
      opcode: 'set',
    })
  },

  renameUser(destId, name) {
    const n = name.trim().slice(0, 32)
    if (!n) return
    get().client?.send(MsgType.UserInfoSet, { destId, name: n, info: '', opcode: 'set' })
  },

  setRelation(destId, bits, on) {
    get().client?.send(MsgType.UserRelationSet, {
      destId,
      relation: bits,
      opcode: on ? 'add' : 'remove',
    })
    // 屏蔽是本机自己的事，先本地生效，收到回执再以服务端为准。
    set((s) => {
      const next = new Map(s.relations)
      const cur = next.get(destId) ?? 0
      next.set(destId, on ? cur | bits : cur & ~bits)
      return { relations: next }
    })
  },

  requestControl() {
    const s = get()
    if (s.sharerId === USER_NONE || s.sharerId === s.selfId) return
    s.client?.send(MsgType.ShareCtrlReq, {})
    set({ toast: '已申请远程协助，等待对方同意' })
  },

  replyControl(destId, agree) {
    get().client?.send(MsgType.ShareCtrlReply, { destId, agree })
    // 授权只能从这里发起 —— 这是本机用户亲手点了「同意」的唯一入口。
    //
    // 以前是等服务端把 share.ctrl.reply 广播回来再 grant，等于「服务端说同意了」
    // 就放行本机键鼠注入。局域网节点模式下服务端可能是任意一台同事的设备，
    // 不能让它替本机用户做这个决定。广播回来那条只用来同步界面。
    if (agree) {
      void grantRemoteControl(destId)
      set({ ctrlRequestFrom: USER_NONE, controllerId: destId })
    } else {
      set({ ctrlRequestFrom: USER_NONE })
    }
  },

  sendControlMouse(ev) {
    const s = get()
    if (s.controllingId === USER_NONE) return
    s.client?.send(MsgType.ShareCtrlMouse, ev)
  },

  sendControlKey(ev) {
    const s = get()
    if (s.controllingId === USER_NONE) return
    s.client?.send(MsgType.ShareCtrlKey, ev)
  },

  setToast(msg) {
    set({ toast: msg })
  },
}))

type SetFn = (fn: (s: RoomState) => Partial<RoomState>) => void
type GetFn = () => RoomState

/** 把服务端事件接到仓库上。 */
function bindEvents(client: Client, set: SetFn, get: GetFn): void {
  client.on('__state', (s) => set(() => ({ conn: s as ConnState })))

  // 信令断过一次。之后来的 room.joined 是自动重连回房，而不是第一次进房。
  let dropped = false
  client.on('__disconnected', () => {
    dropped = true
  })

  client.on<RoomJoined>(MsgType.RoomJoined, (d) => {
    const members = new Map<number, Member>()
    for (const u of d.users) members.set(u.id, toMember(u))

    // 重连回房：服务端在断线那一刻已经拆掉了这个会话的全部媒体（SFU.Detach），
    // 现在是一个全新的 peer —— 它不知道我在推什么，旧的下行轨也全都作废了。
    //   - 自己的麦克风/摄像头按当前开关重新发布，否则工具栏显示开着、别人听不到；
    //   - 旧的远端轨全部丢掉、下面重新订阅，否则断线期间走掉的人留下一条空音频轨；
    //   - 屏幕共享服务端已替我结束，停掉本地采集并说一声；
    //   - 远程控制授权一并收回：控制我的那个人已经不是同一个会话了。
    const reconnected = dropped
    dropped = false
    if (reconnected) {
      const st = get()
      if (st.controllerId !== USER_NONE) void revokeRemoteControl()
      void st.media?.rebuildAfterReconnect().then(({ screenDropped }) => {
        if (screenDropped) set(() => ({ toast: '网络中断过，屏幕共享已停止，如需继续请重新发起' }))
      }).catch((e) => console.warn('[whatylive] 重连后重建媒体失败', e))
      set(() => ({
        remoteTracks: new Map(),
        speaking: new Map(),
        sharePaused: false,
        controllerId: USER_NONE,
        controllingId: USER_NONE,
        ctrlRequestFrom: USER_NONE,
      }))
    }

    set(() => ({
      selfId: d.selfId,
      room: toRoom(d.room),
      // 进房时可能已经有人在共享，界面据此直接切到共享画面。
      sharerId: d.room.sharerId ?? USER_NONE,
      members,
      mics: d.mics ?? [],
      micLocks: d.micLocks ?? [],
      messages: (d.history ?? []).map(toMessage),
      micQueue: [],
    }))

    // 进房后的订阅策略：
    //   语音 —— 全订，课堂上谁说话都要听得见；
    //   视频 —— 主持人与持麦者必订，其余人按房间的 videoNum 名额订。
    // 老系统是纯按需订阅，但它从没提供请求视频的入口，
    // 结果就是学生根本看不到老师。
    const media = get().media
    // 名额要按「已订几路」递增地判，一次性全判会把名额算成 0 而全部放行。
    let videoUsed = 0
    const quota = d.room.videoNum > 0 ? d.room.videoNum : 6
    for (const u of d.users) {
      if (u.id === d.selfId) continue
      media?.subscribe(u.id, ['audio'])
      const must = hasAnyPerm(u.prop, Perm.Op) || u.micId >= 0
      if (must || videoUsed < quota) {
        media?.subscribe(u.id, ['video'])
        videoUsed++
      }
    }
    // 进房时已经有人在共享的话，也要补订这一路 ——
    // rtc.published 是在开始共享的那一刻广播的，后进来的人收不到。
    const sharer = d.room.sharerId ?? USER_NONE
    if (sharer !== USER_NONE && sharer !== d.selfId) {
      media?.subscribe(sharer, ['screen'])
    }
  })

  client.on<UserInfo>(MsgType.UserJoined, (u) =>
    set((s) => {
      const next = new Map(s.members)
      next.set(u.id, toMember(u))
      return { members: next }
    }),
  )

  // ---- 实时字幕 ----
  client.on<CaptionState>(MsgType.CaptionState, (d) =>
    // 关掉时把已显示的字幕一起清掉，免得最后一句一直挂在屏幕上。
    set(() => (d.on ? { captionOn: true } : { captionOn: false, captions: [] })),
  )
  client.on<CaptionText>(MsgType.CaptionText, (d) =>
    set((s) => {
      const line: CaptionLine = { ...d, key: `${d.time}-${d.fromId}-${nextCaptionSeq++}` }
      // 中间结果就地替换同一个人的上一条；定稿则追加。
      const list = [...s.captions]
      if (!d.final && list.length > 0 && list[list.length - 1].fromId === d.fromId && !list[list.length - 1].final) {
        list[list.length - 1] = line
      } else {
        list.push(line)
      }
      // 只留最近几条：字幕是流水，留太多既占地方也没人回头看。
      return { captions: list.slice(-CAPTION_KEEP) }
    }),
  )

  // ---- 分组讨论 ----
  client.on<GroupOpened>(MsgType.GroupOpened, (d) =>
    set((s) => {
      // 按账号找自己那组。席位号在换房间时会重新分配，跨房间对不上。
      const acct = s.selfAccount
      const mine = d.groups.find((g) => g.members.includes(acct)) ?? null
      return { groups: d.groups, myGroup: mine, groupClosed: null }
    }),
  )
  client.on<GroupClosed>(MsgType.GroupClosed, (d) =>
    set(() => ({ groups: [], myGroup: null, groupClosed: d.mainRoomId })),
  )
  client.on<GroupNotified>(MsgType.GroupNotified, (d) => set(() => ({ groupNotice: d.text })))

  // ---- 投票 ----
  client.on<PollStarted>(MsgType.PollStarted, (d) =>
    // 新投票开始时清掉上一轮的选择与统计，否则会显示成「已投过」。
    set(() => ({ poll: d, pollStat: null, myVote: [], pollResult: null })),
  )
  client.on<PollStat>(MsgType.PollStat, (d) => set(() => ({ pollStat: d })))
  client.on<PollEnded>(MsgType.PollEnded, (d) =>
    set(() => ({ poll: null, pollStat: null, myVote: [], pollResult: d })),
  )

  // ---- 课堂签到 ----
  client.on<AttendStarted>(MsgType.AttendStarted, (d) =>
    // 新一轮开始时清掉上一轮的痕迹，否则会显示成「已经签过了」。
    set(() => ({ attend: d, attendStat: null, attendMarked: false, attendResult: null })),
  )
  client.on<AttendStat>(MsgType.AttendStat, (d) => set(() => ({ attendStat: d })))
  client.on<AttendEnded>(MsgType.AttendEnded, (d) =>
    set(() => ({ attend: null, attendStat: null, attendMarked: false, attendResult: d })),
  )

  // ---- 等候室 ----
  client.on<WaitJoined>(MsgType.WaitJoined, (d) => set(() => ({ waiting: d, waitRejected: null })))
  client.on<WaitList>(MsgType.WaitList, (d) => set(() => ({ waitList: d.entries ?? [] })))
  client.on(MsgType.WaitAdmitted, () =>
    set((s) => (s.waiting ? { waiting: { ...s.waiting, admitted: true } } : {})),
  )
  client.on<WaitRejected>(MsgType.WaitRejected, (d) =>
    // 服务端紧接着会断开连接，别再自动重连回门口。
    set((s) => {
      s.client?.close()
      return { waiting: null, waitRejected: d.reason || '主持人拒绝了你的进入请求' }
    }),
  )

  client.on<UserLeft>(MsgType.UserLeft, (d) =>
    set((s) => {
      // 自己被踢/被顶下线：给出明确原因，而不是只显示「连接断开」。
      if (d.id === s.selfId || d.id === undefined) {
        // 服务器关闭是暂时的：发版重启、优雅停机时服务端会如实发这个原因。
        // 以前和「被请出」一样当终局处理 —— 关连接、退回登录页，于是每次发版
        // 全场的人都被踢回登录页。腾讯会议的做法是提示一下、自己重连回来，
        // 这里同样只提示，连接断开后交给 client 的自动重连（rejoin 回原房间）。
        if (d.reason === LeaveReason.ServerShutdown) {
          return { toast: '服务器正在重启，稍后自动重新连接…' }
        }
        const text = LEAVE_REASON_TEXT[d.reason]
        if (d.reason !== 0 && text) {
          // 被请出 / 课程结束 / 顶号：服务端马上会断开，别再自动重连回去。
          s.client?.close()
          return { toast: text, leftReason: d.reason }
        }
      }
      const next = new Map(s.members)
      next.delete(d.id)
      const speaking = new Map(s.speaking)
      speaking.delete(d.id)
      // 人都走了，挂在他身上的私聊邀请与撤回按钮一并收掉。
      const invites = new Map(s.pmInvites)
      invites.delete(d.id)
      // 把他身上的远端轨一并清掉。
      //
      // 不能只靠 track 的 'ended' 事件（media.ts 的 handleRemoteTrack 挂在那里）：
      // 发送方离开时，接收端拿到的通常只有 'mute'，remote track 并不会 ended。
      // 视频那边碰巧看不出来 —— 宫格按成员列表渲染，人从名单里消失格子就没了；
      // 但 AudioSink 是直接遍历 remoteTracks 的，于是走掉的人会留下一条永远在播的
      // 空音频轨。这与 ShareStop 处记的是同一个坑：服务端信号才是权威，
      // 那边用 ShareStop 兜住了，这边漏了 user.left。
      const tracks = new Map(s.remoteTracks)
      for (const [id, t] of tracks) if (t.userId === d.id) tracks.delete(id)
      // 屏蔽关系按席位记，席位会被后来的人复用 —— 不清的话新进来的人一进门就显示「已屏蔽」。
      const relations = new Map(s.relations)
      relations.delete(d.id)
      // 控制本机的那个人走了：授权立刻收回。服务端也会清，但本机不能指望别人替它把门。
      const lostController = s.controllerId === d.id
      if (lostController) void revokeRemoteControl()
      return {
        members: next,
        speaking,
        pmInvites: invites,
        remoteTracks: tracks,
        relations,
        // 排队的人走了还挂在队列里，主持人会把麦给一个已经不在的人。
        micQueue: s.micQueue.filter((q) => q !== d.id),
        pmPending: s.pmPending === d.id ? USER_NONE : s.pmPending,
        pmPeer: s.pmPeer === d.id ? USER_NONE : s.pmPeer,
        controllerId: lostController ? USER_NONE : s.controllerId,
        ctrlRequestFrom: s.ctrlRequestFrom === d.id ? USER_NONE : s.ctrlRequestFrom,
      }
    }),
  )

  client.on<TextRecv>(MsgType.TextRecv, (m) =>
    set((s) => {
      const list = [...s.messages, toMessage(m)]
      // 只保留最近 500 条，避免长课堂把内存吃满。
      return { messages: list.length > 500 ? list.slice(-500) : list }
    }),
  )

  client.on<ReactRecv>(MsgType.ReactRecv, (m) => {
    // 每条表情用「时刻 + 席位」做键：同一个人连点也不会撞键。
    const item: FloatingReaction = { key: `${m.time}-${m.fromId}-${nextReactSeq++}`, ...m }
    set((s) => ({ reactions: [...s.reactions, item].slice(-20) }))
    // 到点自己清掉。停留时间与 CSS 动画时长对齐，
    // 早清会让表情半路消失，晚清则会在原地卡一下。
    setTimeout(() => {
      set((s) => ({ reactions: s.reactions.filter((x) => x.key !== item.key) }))
    }, REACTION_TTL_MS)
  })

  client.on<UserPropChanged>(MsgType.UserPropChanged, (d) =>
    set((s) => ({
      ...patchMember(s, d.destId, (m) => ({ ...m, prop: d.final })),
      // 主持权可能随这次变更易主（授予联席 / 移交）。
      room: s.room && d.ownerId != null ? { ...s.room, ownerId: d.ownerId } : s.room,
    })),
  )

  client.on<UserStateChanged>(MsgType.UserStateChanged, (d) =>
    set((s) => patchMember(s, d.destId, (m) => ({ ...m, state: d.final }))),
  )

  client.on<UserInfoChanged>(MsgType.UserInfoChanged, (d) =>
    set((s) => patchMember(s, d.destId, (m) => ({ ...m, info: d.info, name: d.name || m.name }))),
  )

  client.on<RoomPropChanged>(MsgType.RoomPropChanged, (d) =>
    set((s) => (s.room ? { room: { ...s.room, flags: d.flags } } : {})),
  )

  client.on<MicAssigned>(MsgType.MicAssigned, (d) =>
    set((s) => {
      // 谁上麦就看谁：画面跟着发言人走。
      if (d.destId !== s.selfId) s.media?.subscribe(d.destId, ['video'])
      const mics = [...s.mics]
      if (d.micId >= 0) mics[d.micId] = d.destId
      return {
        // 持麦即在发言：服务端置了 StateSpeaking 但走的是 mic.assigned 这条消息，
        // 不在这里同步的话，界面上的状态位和服务端不一致 ——
        // 「是否在发言」的判断（含自动化测试）就会读到错的值。
        // 拿到麦也意味着手可以放下了，和服务端同步。
        ...patchMember(s, d.destId, (m) => ({
          ...m,
          micId: d.micId,
          state: (m.state | UState.Speaking) & ~UState.HandRaised,
        })),
        mics,
        micQueue: s.micQueue.filter((q) => q !== d.destId),
      }
    }),
  )

  client.on<MicReleased>(MsgType.MicReleased, (d) =>
    set((s) => {
      // 下麦后他不再「必订」，但只有名额已经不够时才退订他的摄像头。
      // 以前无条件退订：名额明明空着，一个人下了麦画面就没了，还得等他重开摄像头才回来。
      const m = s.members.get(d.destId)
      if (d.destId !== s.selfId && m && !hasAnyPerm(m.prop, Perm.Op)) {
        const after = { ...s, ...patchMember(s, d.destId, (x) => ({ ...x, micId: MIC_NONE })) } as RoomState
        if (!shouldSubscribeVideo(after, d.destId)) s.media?.unsubscribe(d.destId, ['video'])
      }
      const mics = [...s.mics]
      if (d.micId >= 0) mics[d.micId] = USER_NONE
      // 下麦后音量不会再更新，残留的数值会让说话指示灯一直亮着 ——
      // 看上去像是这个人一直在讲话，其实他麦都没了。
      const speaking = new Map(s.speaking)
      speaking.delete(d.destId)
      return {
        ...patchMember(s, d.destId, (m) => ({ ...m, micId: MIC_NONE, state: m.state & ~UState.Speaking })),
        mics,
        speaking,
      }
    }),
  )

  client.on<MicLocked>(MsgType.MicLocked, (d) =>
    set((s) => {
      const locks = [...s.micLocks]
      if (d.micId >= 0) locks[d.micId] = d.lock
      return { micLocks: locks }
    }),
  )

  client.on<MicQueued>(MsgType.MicQueued, (d) =>
    set((s) => {
      const q = s.micQueue.filter((x) => x !== d.id)
      q.splice(Math.min(d.order, q.length), 0, d.id)
      return { micQueue: q }
    }),
  )

  client.on<MicNumChanged>(MsgType.MicNumChanged, (d) =>
    set((s) => (s.room ? { room: { ...s.room, micNum: d.num } } : {})),
  )

  client.on<{ userId: number; media: MediaKind[] }>(MsgType.RTCPublished, (d) => {
    // 有人开始推流：语音与屏幕共享一律订阅，摄像头按名额订。
    //
    // 原先摄像头只订「主持人或持麦者」，于是学员点了开启摄像头，
    // 本地预览亮着、别人一个也看不到，而且没有任何提示 —— 从用户角度
    // 就是这个功能坏了。但也不能全订：几十人的班级一进门就会拉满带宽。
    // 房间本来就有 videoNum 这个字段，它的语义正是「同时几路视频」，
    // 拿它当名额，比按身份一刀切合理。
    const s = get()
    const media = s.media
    const wanted: MediaKind[] = d.media.filter((k) => k === 'audio' || k === 'screen')
    if (d.media.includes('video') && shouldSubscribeVideo(s, d.userId)) {
      wanted.push('video')
    }
    if (wanted.length) media?.subscribe(d.userId, wanted)
  })

  client.on<{ userId: number; level: number }>(MsgType.RTCSpeaking, (d) =>
    set((s) => {
      const next = new Map(s.speaking)
      next.set(d.userId, d.level)
      return { speaking: next }
    }),
  )

  client.on<ShareStarted>(MsgType.ShareStarted, (d) => set(() => ({ sharerId: d.userId, sharePaused: false })))
  // 共享方暂停/继续。观众端据此在画面上盖一层「共享已暂停」，否则定格的画面看着像卡了。
  client.on<{ pause: boolean }>(MsgType.SharePause, (d) => set(() => ({ sharePaused: !!d.pause })))
  client.on<PMPeer>(MsgType.ShareStop, (d) => {
    // 共享结束必须同时收回远程控制授权，否则授权会一直挂在那里，
    // 下次共享时对方无需再次征得同意就能直接操作本机。
    void revokeRemoteControl()
    // 我是共享方、但不是我自己停的（主持人停了我的共享）：本地采集和上传也得停。
    // 否则服务端以为结束了，我这边的屏幕却还在采、还在往外推 —— 这是隐私问题；
    // 再点共享时还会覆盖掉 screenStream，旧流永远没人 stop。
    const me = get()
    if (me.sharerId === me.selfId && me.selfId !== USER_NONE && me.media?.hasScreen()) {
      void me.media.unpublish(['screen'])
      if (d?.fromId !== undefined && d.fromId !== me.selfId) {
        set(() => ({ toast: '主持人已停止你的屏幕共享' }))
      }
    }
    set((st) => {
      // 把共享那条远端轨一并清掉。
      //
      // 不能指望 track 的 'ended' 事件：发送方停止共享时，
      // 接收端拿到的通常只有 'mute'，remote track 并不会 ended ——
      // 于是轨一直留在 remoteTracks 里，学员屏幕上挂着一块黑画面
      // 和一行「正在共享：某某」，共享其实早就结束了。
      // 服务端的 ShareStop 才是权威信号，以它为准。
      const next = new Map(st.remoteTracks)
      for (const [id, t] of next) if (t.kind === 'screen') next.delete(id)
      return {
        remoteTracks: next,
        sharerId: USER_NONE,
        sharePaused: false,
        controllerId: USER_NONE,
        controllingId: USER_NONE,
        ctrlRequestFrom: USER_NONE,
      }
    })
  })

  // 远程控制：只有共享方本人会收到这两类指令，由桌面端注入本机。
  // 浏览器里 desktop.ts 的实现是空操作，界面也不会展示相关入口。
  client.on<ShareCtrlMouse>(MsgType.ShareCtrlMouse, (d) => {
    const from = get().controllerId
    if (from !== USER_NONE) void applyRemoteMouse(from, d)
  })
  client.on<ShareCtrlKey>(MsgType.ShareCtrlKey, (d) => {
    const from = get().controllerId
    if (from !== USER_NONE) void applyRemoteKey(from, d)
  })

  client.on<{ destId: number; agree: boolean }>(MsgType.ShareCtrlReply, (d) =>
    set((s) => {
      // 共享方视角：授权已经在 replyControl（本机用户点「同意」）里做过了，
      // 这条广播只是回声，绝不能据此 grant —— 否则「服务端说同意了」就能放行键鼠注入。
      // 收回方向是安全的，照做。
      if (s.sharerId === s.selfId) {
        if (d.agree) return {}
        void revokeRemoteControl()
        return { controllerId: USER_NONE }
      }
      // 申请方视角：destId 是被授权的人，是我才代表申请通过。
      if (d.destId !== s.selfId) return {}
      return {
        controllingId: d.agree ? s.sharerId : USER_NONE,
        toast: d.agree ? '已获得远程协助权限，可直接操作对方桌面' : '对方谢绝了远程协助',
      }
    }),
  )

  client.on<RecordStarted>(MsgType.RecordStarted, (d) =>
    set(() => ({
      recording: { active: d.ok, recordId: d.recordId, elapsed: 0, remaining: 0 },
      toast: d.ok ? '已开始录制' : `录制启动失败：${d.err ?? '未知原因'}`,
    })),
  )

  client.on<RecordTick>(MsgType.RecordTick, (d) =>
    set((s) => ({
      recording: { ...s.recording, elapsed: d.elapsed, remaining: d.remaining },
    })),
  )

  client.on<RecordStopped>(MsgType.RecordStopped, (d) =>
    set(() => ({
      recording: { active: false, elapsed: d.elapsed, remaining: 0, lastUrl: d.url },
      toast: '录制已结束',
    })),
  )

  // ---------- 私聊 ----------

  client.on<PMInvite>(MsgType.PMInvite, (d) =>
    set((s) => {
      const next = new Map(s.pmInvites)
      next.set(d.fromId, d.text ?? '')
      return { pmInvites: next }
    }),
  )

  client.on<PMInviteReply>(MsgType.PMInviteReply, (d) =>
    set((s) => {
      // 服务端会把结果同时发给邀请方与被邀方，两边都要落到同一个对象上。
      const peer = d.fromId === s.selfId ? d.toId : d.fromId
      // 无论同意与否，这条邀请都已有结果，撤回按钮该收起来了。
      if (!d.agree) {
        return {
          pmPending: USER_NONE,
          toast: `${s.members.get(peer)?.name ?? peer} 谢绝了私聊`,
        }
      }
      return {
        pmPeer: peer,
        pmPending: USER_NONE,
        toast: `已与 ${s.members.get(peer)?.name ?? peer} 开始私聊`,
      }
    }),
  )

  client.on<PMPeer>(MsgType.PMCancel, (d) =>
    set((s) => {
      const next = new Map(s.pmInvites)
      next.delete(d.fromId)
      return { pmInvites: next }
    }),
  )

  client.on<PMPeer>(MsgType.PMStop, () =>
    set(() => ({ pmPeer: USER_NONE, toast: '私聊已结束' })),
  )

  // ---------- 屏蔽关系 ----------

  client.on<UserRelationChanged>(MsgType.UserRelationChanged, (d) =>
    set((s) => {
      // 只有「我对别人」的关系才进本地表；别人对我的订阅意向不用记。
      if (d.mainId !== s.selfId) return {}
      const next = new Map(s.relations)
      next.set(d.destId, d.relation)
      return { relations: next }
    }),
  )

  // ---------- 远程控制 ----------

  client.on<PMPeer>(MsgType.ShareCtrlReq, (d) =>
    set((s) => {
      // 只有正在共享的人才会收到控制申请。
      if (s.sharerId !== s.selfId) return {}
      return { ctrlRequestFrom: d.fromId }
    }),
  )

  client.on<{ code: string; msg: string }>(MsgType.Error, (d) => {
    const text = explainError(d.code, d.msg)
    // explainError 返回 null 表示这条不值得打扰用户（如重连时的重复进房）。
    if (text !== null) set(() => ({ toast: text }))
  })
}

/** 更新单个成员，返回可直接 set 的片段。 */
function patchMember(
  s: RoomState,
  id: number,
  fn: (m: Member) => Member,
): Partial<RoomState> {
  const m = s.members.get(id)
  if (!m) return {}
  const next = new Map(s.members)
  next.set(id, fn(m))
  return { members: next }
}

// ---------- 界面自动化用的探针 ----------
//
// 字幕的识别依赖服务端装了 whisper 模型，多数环境没装。没有探针的话
// 「学员端收到开启状态」「字幕层渲染正确」这两件事就只能靠真实语音验证，
// 于是在没有模型的环境里永远测不到 —— 而这两件事跟模型无关，本该能测。
//
// 只读 + 只写本地状态，不发任何网络消息，不改变服务端行为。
declare global {
  interface Window {
    __captionOn?: () => boolean
    __memberState?: (name: string) => { state: number; micId: number } | null
    __pushCaption?: (name: string, text: string, final?: boolean) => void
    /** 把本地的网络等级直接设成某一档。手机弱网提示只能这样测：
     *  CDP 的网络限速只作用于 HTTP，WebRTC 走 UDP 完全不受影响。 */
    __setNet?: (level: 'unknown' | 'good' | 'fair' | 'poor') => void
  }
}
if (typeof window !== 'undefined') {
  window.__captionOn = () => useRoomStore.getState().captionOn
  // 某人的服务端状态位。发言与否只有服务端说了算，
  // 界面上的语音指示点对每个成员都渲染，拿它当判据会得到假绿。
  window.__setNet = (level) => {
    const cur = useRoomStore.getState().net
    useRoomStore.setState({ net: { ...cur, level, text: `测试探针：${level}` } })
  }
  window.__memberState = (name: string) => {
    const m = [...useRoomStore.getState().members.values()].find((x) => x.name === name)
    return m ? { state: m.state, micId: m.micId } : null
  }
  window.__pushCaption = (name, text, final = true) =>
    useRoomStore.setState((s) => ({
      captions: [
        ...s.captions,
        { key: `probe-${nextCaptionSeq++}`, fromId: s.selfId, name, text, final, time: Math.floor(Date.now() / 1000) },
      ].slice(-CAPTION_KEEP),
    }))
}
